import 'knockout.validation';
import * as ko from "knockout";

export namespace ValidationService {
    // Purpose  :   There are occasions where we'll change the context of a page and it'll immediately validate
    //              the form. Whilst this is a good thing, it's also not great when it immediately pops up the
    //              validation messages as soon as you open modal, panel or whatever is being displayed. This will
    //              remove that issue in it's entirety so call it wisely.
    export function HideAllMessages(validationFields: KnockoutObservable<any>[]): void {
        const validationMessages =
            ko.validation.group(validationFields, { deep: true });

        validationMessages.showAllMessages(false);
    }

    // Purpose  :   There are occasions where we'll change the context of a value and it'll immediately validate
    //              group form properties (i.e. where one form properties depends on another to function). Whilst
    //              this is a good thing, it's also not great when it immediately pops up the validation messages
    //              as the linked property is displayed. This will remove that issue in it's entirety so call it
    //              wisely.
    export function HideValidationMessage(value: KnockoutObservable<any>): void {
        if (value.isModified) {
            value.isModified(false);
        }
    }

    export function AddValidationRule(field: KnockoutObservable<any>, validationFields: KnockoutObservable<any>[], rule: any): void {
        field.extend(rule);
        validationFields.push(field);
    }

    // Purpose  :   This is intended to resolve an ordering problem that can arise when you try and initialise a binding that relies on the validation
    //              properties of an observable before any validation is applied (so things like the 'isValid' function don't exist yet).
    //              This can happen in cases where a child UI component handles the validation rules, but it hasn't added them
    //              at the time of binding the parent component.
    export function SetObservableValidatable(field: KnockoutObservable<any>, validationFields?: KnockoutObservable<any>[]): void {
        field.extend({ validatable: true });
        validationFields?.push(field);
    }

    export function RemoveValidationRule(field: KnockoutObservable<any>, validationFields: KnockoutObservable<any>[]): void {
        var index = validationFields.indexOf(field);

        if (index >= 0) {
            validationFields.splice(index, 1);
        }
    }

    // Purpose  :   Checks that the field(s) are valid with forced mutation of the property(s).
    // Usage    :   Use this on functionality which needs to check that the property is valid and to have any invalid
    //              properties display the appropriate validation error.
    // Example  :   This is a perfect candidate for methods which are about to submit a request to the server.
    //              e.g. Pressing the submit button on a form and the action it calls validates the form properties.
    export function IsValid(validationFields: KnockoutObservable<any>[]): boolean {
        let allValid = true;

        ko.utils.arrayForEach(validationFields, (field: KnockoutObservable<any>) => {
            if (field.valueHasMutated !== undefined) {
                field.valueHasMutated();
            }

            if (!field.isValid()) {
                allValid = false;
            }
        });

        return allValid;
    }

    // Purpose  :   Checks that the field(s) are valid with no forced mutation of the property(s).
    // Usage    :   Use this on functionality which only needs to check that the property is valid without having all
    //              validation messages being rendered.
    // Example  :   This is a perfect candidate for computed methods which only need to validate whether all the fields
    //              are valid so that a button is enabled when true.
    export function AreFieldsValid(validationFields: KnockoutObservable<any>[]): boolean {
        let allValid = true;

        ko.utils.arrayForEach(validationFields, (field: KnockoutObservable<any>) => {
            if (!field.isValid()) {
                allValid = false;
            }
        });

        return allValid;
    }

    export const EmailRegex: RegExp = /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
    export function IsValidEmail(email: string): boolean {
        // Treat empty emails as valid; if the email is mandatory there should be a required rule rather than relying on this
        if (email === undefined || email === null || email.length === 0) {
            return true;
        }

        return EmailRegex.test(email);
    }

    export const PostCodeRegex: RegExp = /([A-Za-z]{1,2}[0-9Rr][0-9A-Za-z]?)\s*([0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2})/;
    export function IsValidPostCode(postcode: string): boolean {
        if (postcode === undefined || postcode === null || postcode.length === 0) {
            return true;
        }

        return PostCodeRegex.test(postcode);
    }

    export const SortCodeRegex: RegExp = /^(?!(?:0{6}|00-00-00))(?:\d{6}|\d\d-\d\d-\d\d)$/;
    export const IBANRegex: RegExp = /[a-zA-Z]{2}[0-9]{2}[a-zA-Z0-9]{4}[0-9]{7}([a-zA-Z0-9]?){0,16}/;
    export const BICRegex: RegExp = /^[a-z]{6}[2-9a-z][0-9a-np-z]([a-z0-9]{3}|x{3})?$/i;
    export const BankCodeRegex: RegExp = /^(\d{6})$/;
    export const TransitCodeRegex: RegExp = /^(0\d{8})$/;
}